import { gql } from '@apollo/client'
import { brightspotConfig } from '../../config'
import { brEncodeAttribute, stripHtml, escapeBackslash } from '../helpers'
import {
  DashboardSessionDetail,
  SessionDetail,
  getSessionDetail as getSessionDetailFragment,
} from './fragments/SessionDetail'
import { getDashboardSessionSearch, getSessionSearch } from './fragments/SessionSearch'
import gqlSanitize from './stringUtils'
import { conferenceConfig } from '../../config/experience'

const { defaults } = brightspotConfig.query
const { logoImage, leadThumbnailImage, profileImage, promoImage, thumbnailImage } = brightspotConfig.imageSizes
export function getSessionDetail(includeAudiences = false) {
  return gql(`
    ${getSessionDetailFragment(includeAudiences)}
    query SessionDetail(
      $pathOrId: ID,
      $leadThumbnailImage_crop: Boolean = false,
      $leadThumbnailImage_height: Int = ${defaults.imageSize},
      $leadThumbnailImage_width: Int = ${defaults.imageSize},
      $promoImage_crop: Boolean = false,
      $promoImage_height: Int = ${defaults.imageSize},
      $promoImage_width: Int = ${defaults.imageSize},
      $logoImage_crop: Boolean = false,
      $logoImage_height: Int = ${defaults.imageSize},
      $logoImage_width: Int = ${defaults.imageSize},
      $thumbnailImage_crop: Boolean = false,
      $thumbnailImage_height: Int = ${defaults.imageSize},
      $thumbnailImage_width: Int = ${defaults.imageSize},
      $profileImage_crop: Boolean = false,
      $profileImage_height: Int = ${defaults.imageSize},
      $profileImage_width: Int = ${defaults.imageSize}) {
        SessionDetail(id: $pathOrId) {
          ...SessionDetail
        }
    }
  `)
}

export function getSessionMutation(isDashboard = false) {
  // NOTE: this needs to stay a mutation until BS fixes the bug where searching for a Session immediately after it
  // is created fails.
  return gql(`
    ${isDashboard ? DashboardSessionDetail : SessionDetail}
    mutation SessionMutation(
      $sessionId: ID,
      $locale: String,
      $leadThumbnailImage_crop: Boolean = false,
      $leadThumbnailImage_height: Int = ${defaults.imageSize},
      $leadThumbnailImage_width: Int = ${defaults.imageSize},
      $logoImage_crop: Boolean = false,
      $logoImage_height: Int = ${defaults.imageSize},
      $logoImage_width: Int = ${defaults.imageSize},
      $promoImage_crop: Boolean = false,
      $promoImage_height: Int = ${defaults.imageSize},
      $promoImage_width: Int = ${defaults.imageSize},
      $thumbnailImage_crop: Boolean = false,
      $thumbnailImage_height: Int = ${defaults.imageSize},
      $thumbnailImage_width: Int = ${defaults.imageSize},
      $profileImage_crop: Boolean = false,
      $profileImage_height: Int = ${defaults.imageSize},
      $profileImage_width: Int = ${defaults.imageSize}) {
        SessionMutation(
          _target: {id: $sessionId, locale: $locale}) {
          result {
            ...${isDashboard ? 'DashboardSessionDetail' : 'SessionDetail'}
          }
        }
      }
  `)
}

export function searchSessions(isDashboard = false, includeAudiences = false) {
  return gql(`
    ${isDashboard ? getDashboardSessionSearch(includeAudiences) : getSessionSearch(includeAudiences)}
    query SessionSearch(
      $conferenceYear: [String],
      $sessionDifficulty: [SessionDifficulty],
      $sessionLocation: [SessionLocation],
      $contentLocale: [String],
      $creatorIds: [ID],
      $exhibitorIds: [ID],
      $ids: [String],
      $pageRequest: PaginationInput = ${gqlSanitize(defaults.pageRequest)},
      $visibilityRequest: VisibilityInput = ${gqlSanitize(defaults.visibilityRequest)},
      $logoImage_crop: Boolean = false,
      $logoImage_height: Int = ${defaults.imageSize},
      $logoImage_width: Int = ${defaults.imageSize},
      $thumbnailImage_crop: Boolean = false,
      $thumbnailImage_height: Int = ${defaults.imageSize},
      $thumbnailImage_width: Int = ${defaults.imageSize},
      $profileImage_crop: Boolean = false,
      $profileImage_height: Int = ${defaults.imageSize},
      $profileImage_width: Int = ${defaults.imageSize}) {
        SessionSearch(
          creators: $creatorIds,
          exhibitors: $exhibitorIds,
          ids: $ids
          pageRequest: $pageRequest,       
          visibilityRequest: $visibilityRequest
          conferenceYear: $conferenceYear
          sessionDifficulty: $sessionDifficulty
          sessionLocation: $sessionLocation
          contentLocale: $contentLocale) {
            ...${isDashboard ? 'DashboardSessionSearch' : 'SessionSearch'}
        }
      }
  `)
}

export function mutateSession() {
  const query = `
  ${DashboardSessionDetail}
  mutation SessionMutation(
    $audiences: [ID],
    $classSyllabus: ID,
    $conferenceYear: String,
    $contentLocale: String,
    $coSpeakers: String,
    $countries: [ID],
    $countryTags: [ID],
    $creators: [ID],
    $description: String,
    $exhibitors: [ID],
    $previousSessionLink: String,
    $publishSlides: Boolean,
    $sessionApplication: String,
    $sessionDeliverable: SessionDeliverable,
    $sessionDifficulty: SessionDifficulty,
    $sessionFlexibility: SessionFlexibility,
    $sessionFormat: SessionFormat,
    $sessionId: String,
    $sessionLocation: sessionLocation,
    $sessionOutline: String,
    $sessionPrerequisites: String,
    $sessionProblem: String,
    $sessionTechnology: SessionTechnology,
    $sessionType: SessionTypeInput,
    $slideDeck: ID,
    $speakerQualifications: String
    $target: MutationTargetInput,
    $tags: [String],
    $title: String,
    $video: ID,
    $slate: slate,
    $workflow: WorkflowInput,
    $leadThumbnailImage_crop: Boolean = false,
    $leadThumbnailImage_height: Int = ${defaults.imageSize},
    $leadThumbnailImage_width: Int = ${defaults.imageSize},
    $logoImage_crop: Boolean = false,
    $logoImage_height: Int = ${defaults.imageSize},
    $logoImage_width: Int = ${defaults.imageSize},
    $promoImage_crop: Boolean = false,
    $promoImage_height: Int = ${defaults.imageSize},
    $promoImage_width: Int = ${defaults.imageSize},
    $thumbnailImage_crop: Boolean = false,
    $thumbnailImage_height: Int = ${defaults.imageSize},
    $thumbnailImage_width: Int = ${defaults.imageSize},
    $profileImage_crop: Boolean = false,
    $profileImage_height: Int = ${defaults.imageSize},
    $profileImage_width: Int = ${defaults.imageSize}) {
      SessionMutation(
        _target: $target,
        _workflow: $workflow,
        audiences: $audiences,
        classSyllabus: $classSyllabus,
        conferenceYear: $conferenceYear,
        contentLocale: $contentLocale,
        coSpeakers: $coSpeakers,
        countries: $countries,
        countryTags: $countryTags,
        creators: $creators,
        description: $description,
        exhibitors: $exhibitors,
        previousSessionLink: $previousSessionLink,
        publishSlides: $publishSlides,
        sessionApplication: $sessionApplication,
        sessionId: $sessionId,
        sessionDeliverable: $sessionDeliverable,
        sessionDifficulty: $sessionDifficulty,
        sessionFlexibility: $sessionFlexibility,
        sessionFormat: $sessionFormat,
        sessionLocation: $sessionLocation,
        sessionOutline: $sessionOutline,
        sessionPrerequisites: $sessionPrerequisites,
        sessionProblem: $sessionProblem,
        sessionTechnology: $sessionTechnology,
        sessionType: $sessionType,
        slideDeck: $slideDeck,
        speakerQualifications: $speakerQualifications,
        tags: $tags,
        title: $title,
        video: $video,
        slate: $slate ) {
          result {
            ...DashboardSessionDetail
          }
        }
    }`

  return gql(query)
}

export async function deleteSession(client, sessionId, contentLocale) {
  // revisit once the new upload endpoint is working correctly
  // deleteArtifacts(sessionId)
  const variables = {
    target: { id: sessionId, locale: contentLocale },
    workflow: { action: 'DELETE' },
  }
  const response = await client.mutate({ mutation: mutateSession(), variables })
  return response?.data?.SessionMutation?.result
}

export async function getSession({ client, sessionId, locale, isDashboard }) {
  const variables = {
    sessionId,
    locale,
    ...logoImage,
    ...leadThumbnailImage,
    ...profileImage,
    ...promoImage,
    ...thumbnailImage,
  }
  const response = await client.mutate({ mutation: getSessionMutation(isDashboard), variables })
  return response?.data?.SessionMutation?.result
}

export function getSessions({ client, params = {} }) {
  const variables = {
    creatorIds: params.creatorIds,
    exhibitorIds: params.exhibitorIds,
  }

  return client.query({ query: searchSessions(), variables })
}

function mapSimpleSessionFields(session) {
  const simpleFieldMap = {
    sessionId: 'sessionId',
    slate: 'slate',
    sessionLocation: 'sessionLocation',
    publishSlides: 'publishSlides',
    sessionOutline: 'sessionOutline',
    sessionProblem: 'sessionProblem',
    sessionPrerequisites: 'sessionPrerequisites',
    sessionTechnology: 'sessionTechnology',
    sessionDeliverable: 'sessionDeliverable',
    sessionDifficulty: 'sessionDifficulty',
    speakerQualifications: 'speakerQualifications',
    countryTags: 'countryTags',
    previousSessionLink: 'previousSessionLink',
    coSpeakers: 'coSpeakers',
    sessionFormat: 'sessionFormat',
    sessionFlexibility: 'sessionFlexibility',
    tags: 'tags',
    sessionApplication: 'sessionApplication',
  }

  const mappedFields = {}
  Object.entries(simpleFieldMap).forEach(([sourceKey, targetKey]) => {
    if (session[sourceKey]) {
      mappedFields[targetKey] = session[sourceKey]
    }
  })
  return mappedFields
}

function buildSessionVariables(session, conferenceYear) {
  const variables = {
    classSyllabus: session.syllabusAttachmentId,
    slideDeck: session.slidesAttachmentId,
    title: escapeBackslash(session.title),
    ...leadThumbnailImage,
    ...logoImage,
    ...profileImage,
    ...promoImage,
    ...thumbnailImage,
    ...mapSimpleSessionFields(session),
  }

  if (conferenceYear !== undefined && conferenceYear !== null && conferenceYear !== '') {
    variables.conferenceYear = String(conferenceYear)
  }
  if (session.description) {
    variables.description = brEncodeAttribute(escapeBackslash(stripHtml(session.description)))
  }
  if (session.sessionType) {
    variables.sessionType = { type: session.sessionType }
  }
  if (session.videoId !== undefined) {
    variables.video = session.videoId
  }
  if (session.transition && session.transition !== '') {
    variables.workflow = {
      action: 'TRANSITION',
      targetState: session.transition === 'next' ? { next: true } : { state: session.transition },
    }
  }
  if (session.creatorId) {
    variables.creators = [session.creatorId]
  }
  if (session.creatorIds) {
    variables.creators = session.creatorIds
  }
  if (session.exhibitorId) {
    variables.exhibitors = [session.exhibitorId]
  }
  // If we have a session id we are doing an update.  Otherwise, it is a create
  if (session.id) {
    variables.target = { id: session.id, locale: session.locale }
  } else {
    variables.contentLocale = session.locale ? session.locale : ''
  }
  // This is the case when the session is sponsored for a creator
  // If the exhibitor is empty we can safely send the empty string to remove it
  if ((session.exhibitor || session.exhibitor === '') && !session.exhibitorId) {
    variables.exhibitors = [session.exhibitor]
  }
  if ((session.creator || session.creator === '') && !session.creatorId) {
    variables.creators = [session.creator]
  }
  if (session.audiences) {
    variables.audiences = session.audiences
  }
  return variables
}

export async function createSession({ client, session, conferenceYear = conferenceConfig.startYear }) {
  const variables = buildSessionVariables(session, conferenceYear)
  const response = await client.mutate({ mutation: mutateSession(), variables })
  return response?.data?.SessionMutation?.result
}

export async function updateSession({ client, session, conferenceYear }) {
  return createSession({ client, session, conferenceYear })
}

export async function archiveSession({ client, session }) {
  const variables = {
    target: { id: session.id, locale: session.contentLocale },
    workflow: { action: 'DELETE' },
    title: escapeBackslash(session.title),
    ...leadThumbnailImage,
    ...logoImage,
    ...profileImage,
    ...promoImage,
    ...thumbnailImage,
  }

  const response = await client.mutate({ mutation: mutateSession(), variables })
  return response?.data?.SessionMutation?.result
}
